// Navbar.jsx - Complete Working Search with Loyal Vault Button
import "./Navbar.css";
import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Container } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { getCartCount, getWishlistCount } from "../utils/cartUtils";
import axios from "axios";
import { 
  FaShoppingBag, 
  FaHome, 
  FaHeart, 
  FaSearch,
  FaBars,
  FaTimes,
  FaTshirt,
  FaPhone,
  FaMapMarkerAlt,
  FaStore,
  FaFire,
  FaTag,
  FaTruck,
  FaInstagram,
  FaFacebook,
  FaYoutube,
  FaTiktok,
  FaRegClock,
  FaGift,
  FaPercent,
  FaSpinner,
  FaEye,
  FaTimesCircle,
  FaCrown
} from "react-icons/fa";
import { MdNewReleases } from "react-icons/md";

const API_BASE = "https://whats.asbfashion.com/api";

const Navbar = () => {
  const [cartCount, setCartCount] = useState(getCartCount());
  const [wishlistCount, setWishlistCount] = useState(getWishlistCount());
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [recentSearches, setRecentSearches] = useState([]);
  const [isLogoLoading, setIsLogoLoading] = useState(true);
  const [isLogoError, setIsLogoError] = useState(false);
  const [mobileSearchFocused, setMobileSearchFocused] = useState(false);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved).slice(0, 5));
      } catch(e) {
        console.error("Error loading recent searches:", e);
      }
    }
  }, []);

  // Save recent search
  const saveRecentSearch = (query) => {
    if (!query || !query.trim()) return;
    const trimmedQuery = query.trim();
    const updated = [trimmedQuery, ...recentSearches.filter(s => s !== trimmedQuery)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  // Update counts from localStorage
  useEffect(() => {
    const updateCounts = () => {
      setCartCount(getCartCount());
      setWishlistCount(getWishlistCount());
    };
    
    window.addEventListener("cartUpdated", updateCounts);
    window.addEventListener("wishlistUpdated", updateCounts);
    
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 20);
      
      if (currentScrollY < lastScrollY || currentScrollY < 50) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        if (window.innerWidth <= 992) {
          setIsVisible(false);
        }
      }
      
      setLastScrollY(currentScrollY);
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      window.removeEventListener("cartUpdated", updateCounts);
      window.removeEventListener("wishlistUpdated", updateCounts);
      window.removeEventListener("scroll", handleScroll);
      document.body.style.overflow = 'unset';
    };
  }, [lastScrollY, isMobileMenuOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsVisible(true);
    setShowSearchDropdown(false);
    setSearchQuery("");
  }, [location]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      setIsVisible(true);
    }
  }, [isMobileMenuOpen]);

  // Handle click outside to close search dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchDropdown(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced search function
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      if (searchQuery && searchQuery.trim().length >= 1) {
        performSearch(searchQuery);
      } else {
        setSearchResults([]);
        if (!searchQuery || searchQuery.length === 0) {
          setShowSearchDropdown(recentSearches.length > 0);
        } else {
          setShowSearchDropdown(false);
        }
      }
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, recentSearches.length]);

  const performSearch = async (query) => {
    if (!query || !query.trim()) return;
    
    setIsSearching(true);
    try {
      const response = await axios.get(`${API_BASE}/search_products.php`, {
        params: { 
          q: query.trim(),
          limit: 10
        },
        timeout: 10000
      });
      
      if (response.data.success) {
        const products = response.data.products || [];
        setSearchResults(products);
        setShowSearchDropdown(true);
      } else {
        setSearchResults([]);
        console.error("Search API error:", response.data.message);
      }
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery && searchQuery.trim()) {
      saveRecentSearch(searchQuery);
      setShowSearchDropdown(false);
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleProductClick = (productId) => {
    setShowSearchDropdown(false);
    setSearchQuery("");
    navigate(`/product/${productId}`);
  };

  const handleRecentSearchClick = (query) => {
    setSearchQuery(query);
    saveRecentSearch(query);
    setShowSearchDropdown(false);
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setShowSearchDropdown(false);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const formatPrice = (price) => {
    if (!price) return "LKR 0";
    return `LKR ${parseFloat(price).toLocaleString()}`;
  };

  const navItems = [
    { path: "/", label: "Home", icon: FaHome },
    { path: "/men", label: "Men", icon: FaTshirt },
    { path: "/women", label: "Women", icon: FaTshirt },
    { path: "/kids", label: "Kids", icon: FaTshirt },
    { path: "/lookbook", label: "Lookbook", icon: MdNewReleases },
    { path: "/stores", label: "Stores", icon: FaStore }
  ];

  const isActivePath = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const cartBadgeVariants = {
    initial: { scale: 0, rotate: -180 },
    animate: { 
      scale: 1, 
      rotate: 0,
      transition: { type: "spring", stiffness: 300, damping: 15 }
    },
    pulse: {
      scale: [1, 1.3, 1],
      transition: { duration: 0.4, repeat: 1 }
    }
  };

  const mobileMenuVariants = {
    closed: {
      opacity: 0,
      x: "100%",
      transition: { duration: 0.3, ease: "easeInOut" }
    },
    open: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.3, ease: "easeInOut" }
    }
  };

  return (
    <>
      <motion.div
        className={`asb-navbar ${isScrolled ? 'asb-navbar-scrolled' : ''} ${!isVisible ? 'asb-navbar-hidden' : ''}`}
        initial={{ y: 0 }}
        animate={{ 
          y: isVisible ? 0 : -120,
          transition: { duration: 0.3, ease: "easeInOut" }
        }}
      >
        {/* Top Announcement Bar */}
        <div className="asb-top-bar">
          <Container fluid className="px-3 px-lg-4">
            <div className="asb-top-bar-content">
              <div className="asb-top-bar-left">
                <motion.div className="asb-top-item" whileHover={{ y: -2 }}>
                  <FaRegClock className="asb-top-icon" />
                  <span>9AM - 9PM Daily</span>
                </motion.div>
                <span className="asb-top-divider">|</span>
                <motion.div className="asb-top-item" whileHover={{ y: -2 }}>
                  <FaPhone className="asb-top-icon" />
                  <span>071 905 7057</span>
                </motion.div>
              </div>
              <div className="asb-top-bar-right">
                <Link to="/about" className="asb-top-link">About Us</Link>
                <Link to="/stores" className="asb-top-link">Store Locator</Link>
                <div className="asb-social-icons">
                  <motion.a whileHover={{ scale: 1.1 }} href="https://www.instagram.com/asbfashionsl?igsh=MXdicXBvcHdndGs0Mg==" target="_blank" rel="noopener noreferrer"><FaInstagram /></motion.a>
                  <motion.a whileHover={{ scale: 1.1 }} href="https://www.facebook.com/share/18fvBYoCn5/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer"><FaFacebook /></motion.a>
                  <motion.a whileHover={{ scale: 1.1 }} href="https://www.tiktok.com/@asbfashion3?_r=1&_t=ZS-969e9ly6lE4" target="_blank" rel="noopener noreferrer"><FaTiktok /></motion.a>
                </div>
              </div>
            </div>
          </Container>
        </div>

        {/* Main Navbar */}
        <div className="asb-main-nav">
          <Container fluid className="px-3 px-lg-4">
            <div className="asb-nav-wrapper">
              {/* Logo */}
              <motion.div
                className="asb-logo-wrapper"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Link to="/" className="asb-logo">
                  <div className="asb-logo-icon">
                    {isLogoLoading && !isLogoError && (
                      <div className="logo-loading-wrapper" aria-hidden="true">
                        <div className="logo-loader-ring"></div>
                      </div>
                    )}
                    {!isLogoError ? (
                      <img 
                        src="/logo.png" 
                        alt="ASB Fashion" 
                        className="asb-logo-img"
                        style={{ display: isLogoLoading ? 'none' : 'block' }}
                        onLoad={() => setIsLogoLoading(false)}
                        onError={() => {
                          setIsLogoLoading(false);
                          setIsLogoError(true);
                        }}
                      />
                    ) : (
                      <span className="asb-logo-fallback">ASB</span>
                    )}
                  </div>
                  <div className="asb-logo-text">
                    <span className="asb-logo-main">ASB<span className="asb-logo-highlight">FASHION</span></span>
                    <span className="asb-logo-tagline">Beyond Tradition</span>
                  </div>
                </Link>
              </motion.div>

              {/* Desktop Search with Dynamic Results - Enhanced */}
              <div className="asb-search-container d-none d-lg-flex" ref={searchRef}>
                <motion.form 
                  onSubmit={handleSearchSubmit} 
                  className={`asb-search-form ${isSearchFocused ? 'focused' : ''}`}
                  animate={{ width: isSearchFocused ? '500px' : '380px' }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  <div className="asb-search-input-wrapper">
                    <FaSearch className="search-icon" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search for fashion, accessories..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => {
                        setIsSearchFocused(true);
                        if (searchResults.length > 0 || recentSearches.length > 0) {
                          setShowSearchDropdown(true);
                        }
                      }}
                      onBlur={() => setIsSearchFocused(false)}
                      className="asb-search-input"
                      autoComplete="off"
                    />
                    {searchQuery && (
                      <button type="button" className="search-clear" onClick={clearSearch} aria-label="Clear search">
                        <FaTimesCircle />
                      </button>
                    )}
                  </div>
                  <motion.button 
                    type="submit" 
                    className="asb-search-btn"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    disabled={isSearching}
                  >
                    {isSearching ? <FaSpinner className="spinner" /> : "Search"}
                  </motion.button>
                  
                  {/* Search Results Dropdown */}
                  <AnimatePresence>
                    {showSearchDropdown && (
                      <motion.div
                        className="search-results-dropdown"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        {isSearching ? (
                          <div className="search-loading">
                            <FaSpinner className="spinner" />
                            <span>Searching products...</span>
                          </div>
                        ) : searchResults.length > 0 ? (
                          <>
                            <div className="search-results-header">
                              <span>{searchResults.length} products found for "{searchQuery}"</span>
                              <button onClick={() => {
                                saveRecentSearch(searchQuery);
                                setShowSearchDropdown(false);
                                navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
                              }}>
                                View All Results
                              </button>
                            </div>
                            <div className="search-results-list">
                              {searchResults.slice(0, 6).map((product) => (
                                <motion.div
                                  key={product.id}
                                  className="search-result-item"
                                  whileHover={{ backgroundColor: 'rgba(220, 38, 38, 0.05)' }}
                                  onClick={() => handleProductClick(product.id)}
                                >
                                  <div className="result-image">
                                    <img 
                                      src={product.image_url || '/images/placeholder.jpg'} 
                                      alt={product.name}
                                      onError={(e) => {
                                        e.target.src = '/images/placeholder.jpg';
                                      }}
                                    />
                                  </div>
                                  <div className="result-info">
                                    <h4>{product.name}</h4>
                                    <div className="result-meta">
                                      {product.brand && <span className="brand">{product.brand}</span>}
                                      <span className="price">{formatPrice(product.price)}</span>
                                      {product.old_price && parseFloat(product.old_price) > parseFloat(product.price) && (
                                        <span className="old-price">{formatPrice(product.old_price)}</span>
                                      )}
                                    </div>
                                    {product.discount_percent > 0 && (
                                      <span className="discount-badge">-{product.discount_percent}%</span>
                                    )}
                                  </div>
                                  <div className="result-action">
                                    <FaEye />
                                  </div>
                                </motion.div>
                              ))}
                            </div>
                            {searchResults.length > 6 && (
                              <div className="search-results-footer">
                                <button onClick={() => {
                                  saveRecentSearch(searchQuery);
                                  setShowSearchDropdown(false);
                                  navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
                                }}>
                                  +{searchResults.length - 6} more results
                                </button>
                              </div>
                            )}
                          </>
                        ) : recentSearches.length > 0 && (!searchQuery || searchQuery.length === 0) ? (
                          <div className="recent-searches">
                            <div className="recent-header">
                              <span>Recent Searches</span>
                              <button onClick={clearRecentSearches}>Clear All</button>
                            </div>
                            {recentSearches.map((query, idx) => (
                              <div
                                key={idx}
                                className="recent-search-item"
                                onClick={() => handleRecentSearchClick(query)}
                              >
                                <FaSearch />
                                <span>{query}</span>
                              </div>
                            ))}
                          </div>
                        ) : searchQuery && searchQuery.length > 0 && searchResults.length === 0 && !isSearching ? (
                          <div className="no-results">
                            <FaSearch />
                            <p>No products found for "{searchQuery}"</p>
                            <button onClick={() => {
                              saveRecentSearch(searchQuery);
                              setShowSearchDropdown(false);
                              navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
                            }}>
                              Browse All Products
                            </button>
                          </div>
                        ) : null}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.form>
              </div>

              {/* Actions */}
              <div className="asb-actions">
                <motion.div className="asb-action-item" whileHover={{ y: -2 }} whileTap={{ scale: 0.95 }}>
                  <Link to="/wishlist" className="asb-action-link">
                    <FaHeart className="asb-action-icon" />
                    <AnimatePresence mode="wait">
                      {wishlistCount > 0 && (
                        <motion.span
                          className="asb-action-badge"
                          variants={cartBadgeVariants}
                          initial="initial"
                          animate="animate"
                        >
                          {wishlistCount > 99 ? '99+' : wishlistCount}
                        </motion.span>
                      )}
                    </AnimatePresence>
                    <span className="asb-action-label">Wishlist</span>
                  </Link>
                </motion.div>

                {/* Loyal Vault Button */}
                <motion.div className="asb-action-item" whileHover={{ y: -2 }} whileTap={{ scale: 0.95 }}>
                  <a
                    href="https://customer.asbfashion.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="asb-action-link asb-loyal-link"
                  >
                    <FaCrown className="asb-action-icon asb-loyal-icon" />
                    <span className="asb-action-label">Loyal Vault</span>
                  </a>
                </motion.div>

                <motion.div className="asb-action-item" whileHover={{ y: -2 }} whileTap={{ scale: 0.95 }}>
                  <Link to="/cart" className="asb-action-link">
                    <FaShoppingBag className="asb-action-icon" />
                    <AnimatePresence mode="wait">
                      {cartCount > 0 && (
                        <motion.span
                          className="asb-action-badge asb-cart-badge"
                          variants={cartBadgeVariants}
                          initial="initial"
                          animate="animate"
                          whileHover="pulse"
                        >
                          {cartCount > 99 ? '99+' : cartCount}
                        </motion.span>
                      )}
                    </AnimatePresence>
                    <span className="asb-action-label">Cart</span>
                  </Link>
                </motion.div>

                <motion.button
                  className="asb-mobile-toggle d-lg-none"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label="Toggle menu"
                >
                  <AnimatePresence mode="wait">
                    {isMobileMenuOpen ? (
                      <motion.div
                        key="close"
                        initial={{ rotate: -90, opacity: 0 }}
                        animate={{ rotate: 0, opacity: 1 }}
                        exit={{ rotate: 90, opacity: 0 }}
                      >
                        <FaTimes />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="menu"
                        initial={{ rotate: 90, opacity: 0 }}
                        animate={{ rotate: 0, opacity: 1 }}
                        exit={{ rotate: -90, opacity: 0 }}
                      >
                        <FaBars />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="asb-nav-menu d-none d-lg-block">
              <div className="asb-nav-items">
                {navItems.map((item, index) => {
                  const IconComponent = item.icon;
                  const isActive = isActivePath(item.path);
                  return (
                    <motion.div
                      key={item.path}
                      className="asb-nav-item-wrapper"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                    >
                      <Link
                        to={item.path}
                        className={`asb-nav-link ${isActive ? 'active' : ''}`}
                      >
                        <IconComponent className="asb-nav-icon" />
                        <span>{item.label}</span>
                        {isActive && (
                          <motion.div
                            className="asb-nav-indicator"
                            layoutId="activeIndicator"
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                          />
                        )}
                      </Link>
                    </motion.div>
                  );
                })}
                
                <motion.div
                  className="asb-nav-item-wrapper"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: navItems.length * 0.03 }}
                >
                  <Link
                    to="/sale"
                    className={`asb-nav-link asb-sale-link ${isActivePath('/sale') ? 'active' : ''}`}
                  >
                    <FaFire className="asb-nav-icon" />
                    <span>SALE</span>
                    <FaTag className="asb-sale-icon" />
                    {isActivePath('/sale') && (
                      <motion.div
                        className="asb-nav-indicator"
                        layoutId="activeIndicator"
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                      />
                    )}
                  </Link>
                </motion.div>

                <motion.div
                  className="asb-nav-item-wrapper"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: (navItems.length + 1) * 0.03 }}
                >
                  <Link
                    to="/gift-voucher"
                    className={`asb-nav-link ${isActivePath('/gift-voucher') ? 'active' : ''}`}
                  >
                    <FaGift className="asb-nav-icon" />
                    <span>Gift Card</span>
                    {isActivePath('/gift-voucher') && (
                      <motion.div
                        className="asb-nav-indicator"
                        layoutId="activeIndicator"
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                      />
                    )}
                  </Link>
                </motion.div>
              </div>
            </div>
          </Container>
        </div>

        {/* ========== MOBILE SEARCH BAR – ENHANCED ========== */}
        <div className="asb-mobile-search d-lg-none">
          <Container fluid className="px-3">
            <form onSubmit={handleSearchSubmit} className="asb-mobile-search-form">
              <div className={`asb-mobile-search-wrapper ${mobileSearchFocused || (searchQuery && searchQuery.length > 0) ? 'expanded' : ''}`}>
                <FaSearch className="mobile-search-icon" />
                <input
                  type="text"
                  placeholder="Search for fashion, accessories..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setMobileSearchFocused(true)}
                  onBlur={() => setMobileSearchFocused(false)}
                  className="asb-mobile-search-input"
                  autoComplete="off"
                />
                {searchQuery && (
                  <button type="button" className="mobile-search-clear" onClick={clearSearch} aria-label="Clear search">
                    <FaTimesCircle />
                  </button>
                )}
              </div>
              <button 
                type="submit" 
                className="asb-mobile-search-btn" 
                disabled={isSearching}
                aria-label="Search"
              >
                {isSearching ? <FaSpinner className="spinner" /> : <FaSearch />}
              </button>
            </form>
          </Container>
        </div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <>
              <motion.div
                className="asb-mobile-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsMobileMenuOpen(false)}
              />
              
              <motion.div
                className="asb-mobile-menu"
                variants={mobileMenuVariants}
                initial="closed"
                animate="open"
                exit="closed"
              >
                <div className="asb-mobile-header">
                  <div className="asb-mobile-logo">
                    <div className="asb-mobile-logo-image-wrap">
                      {isLogoLoading && !isLogoError && (
                        <div className="logo-loading-wrapper asb-mobile-logo-loader" aria-hidden="true">
                          <div className="logo-loader-ring"></div>
                        </div>
                      )}
                      {!isLogoError ? (
                        <img 
                          src="/logo.png" 
                          alt="ASB Fashion" 
                          className="asb-mobile-logo-img"
                          style={{ display: isLogoLoading ? 'none' : 'block' }}
                          onLoad={() => setIsLogoLoading(false)}
                          onError={() => {
                            setIsLogoLoading(false);
                            setIsLogoError(true);
                          }}
                        />
                      ) : (
                        <div className="asb-mobile-logo-fallback">ASB</div>
                      )}
                    </div>
                    <div>
                      <h3 className="asb-mobile-logo-text">ASB FASHION</h3>
                      <p className="asb-mobile-logo-tagline">Beyond Tradition</p>
                    </div>
                  </div>
                </div>

                <div className="asb-mobile-nav">
                  <div className="asb-mobile-nav-section">
                    <h4 className="asb-mobile-section-title">Menu</h4>
                    {navItems.map((item) => {
                      const IconComponent = item.icon;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`asb-mobile-nav-link ${isActivePath(item.path) ? 'active' : ''}`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <IconComponent className="asb-mobile-nav-icon" />
                          <span>{item.label}</span>
                        </Link>
                      );
                    })}
                    <Link
                      to="/sale"
                      className={`asb-mobile-nav-link asb-mobile-sale-link ${isActivePath('/sale') ? 'active' : ''}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <FaFire className="asb-mobile-nav-icon" />
                      <span>Sale</span>
                      <FaPercent className="ms-auto" />
                    </Link>
                    <Link
                      to="/gift-voucher"
                      className={`asb-mobile-nav-link ${isActivePath('/gift-voucher') ? 'active' : ''}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <FaGift className="asb-mobile-nav-icon" />
                      <span>Gift Cards</span>
                    </Link>

                    {/* Loyal Vault (Mobile) */}
                    <a
                      href="https://customer.asbfashion.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="asb-mobile-nav-link"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <FaCrown className="asb-mobile-nav-icon" />
                      <span>Loyal Vault</span>
                    </a>
                  </div>

                  <div className="asb-mobile-nav-section">
                    <h4 className="asb-mobile-section-title">Information</h4>
                    <Link to="/about" className="asb-mobile-nav-link">About Us</Link>
                    <Link to="/contact" className="asb-mobile-nav-link">Contact Us</Link>
                    <Link to="/stores" className="asb-mobile-nav-link">Store Locator</Link>
                  </div>

                  <div className="asb-mobile-nav-section">
                    <h4 className="asb-mobile-section-title">Contact Info</h4>
                    <div className="asb-mobile-contact-item">
                      <FaPhone className="asb-mobile-contact-icon" />
                      <span>071 905 7057</span>
                    </div>
                    <div className="asb-mobile-contact-item">
                      <FaMapMarkerAlt className="asb-mobile-contact-icon" />
                      <span>17 Branches Islandwide</span>
                    </div>
                   
                    <div className="asb-mobile-contact-item">
                      <FaRegClock className="asb-mobile-contact-icon" />
                      <span>9AM - 9PM Daily</span>
                    </div>
                  </div>

                  <div className="asb-mobile-social">
                    <motion.a whileHover={{ scale: 1.1 }} href="https://www.instagram.com/asbfashionsl?igsh=MXdicXBvcHdndGs0Mg==" target="_blank" rel="noopener noreferrer"><FaInstagram /></motion.a>
                    <motion.a whileHover={{ scale: 1.1 }} href="https://www.facebook.com/share/18fvBYoCn5/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer"><FaFacebook /></motion.a>
                    <motion.a whileHover={{ scale: 1.1 }} href="https://www.tiktok.com/@asbfashion3?_r=1&_t=ZS-969e9ly6lE4" target="_blank" rel="noopener noreferrer"><FaTiktok /></motion.a>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Spacer to prevent content jump */}
      <div className="asb-navbar-spacer" />
    </>
  );
};

export default Navbar;